// frontend/src/components/profile/StoreOrders.js
import React, { useState, useEffect } from 'react';
import { ordersApi } from '../../api/orders';
import uploadService from '../../services/upload';
import { compressImage } from '../../utils/imageCompression';
import helpers from '../../utils/helpers';
import CameraModal from '../common/CameraModal';
import { 
    FaBox, FaTruck, FaClock, FaCheckCircle, 
    FaMapMarkerAlt, FaCamera, FaSpinner, FaTimes, FaPaperPlane, FaInfoCircle, FaFolderOpen 
} from 'react-icons/fa';

function StoreOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal State
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Camera State
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  // Form State
  const [logisticsData, setLogisticsData] = useState({
      carrier_name: '',
      tracking_number: '',
      pickup_terminal: '',
      proof_url: ''
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await ordersApi.getAllOrdersAdmin();
      // Filter for active orders that need shipping
      setOrders(data);
    } catch (err) {
      console.error("Failed to load store orders", err);
    } finally {
      setLoading(false);
    }
  };

  // --- SLA TIMER COMPONENT ---
  const SlaTimer = ({ deadline, status }) => {
      const [timeLeft, setTimeLeft] = useState('');
      const [isOverdue, setIsOverdue] = useState(false);

      useEffect(() => {
          if (status === 'shipped' || status === 'delivered') return;

          const calculateTime = () => {
              const now = new Date();
              const end = new Date(deadline);
              const diff = end - now;

              if (diff <= 0) {
                  setIsOverdue(true);
                  setTimeLeft("OVERDUE");
              } else {
                  const hours = Math.floor(diff / (1000 * 60 * 60));
                  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                  setTimeLeft(`${hours}h ${minutes}m`);
              }
          };

          calculateTime();
          const timer = setInterval(calculateTime, 60000); // Update every minute
          return () => clearInterval(timer);
      }, [deadline, status]);

      if (status === 'shipped' || status === 'delivered') {
          return <span className="text-green-600 font-bold text-xs flex items-center gap-1"><FaCheckCircle /> Fulfilled</span>;
      }

      return (
          <div className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded ${isOverdue ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-700'}`}>
              <FaClock /> {isOverdue ? "OVERDUE" : timeLeft}
          </div>
      );
  };

  // --- HANDLERS ---

  const handleOpenModal = (order) => {
      setSelectedOrder(order);
      setLogisticsData({ carrier_name: '', tracking_number: '', pickup_terminal: '', proof_url: '' });
      setIsModalOpen(true);
  };

  // Unified File Processor (Handles both Camera and Upload)
  const processFile = async (file) => {
      if (!file) return;

      setUploading(true);
      try {
          const compressed = await compressImage(file, 0.5);
          const res = await uploadService.uploadImage(compressed, 'waybills');
          setLogisticsData(prev => ({ ...prev, proof_url: res.url }));
      } catch (err) {
          alert("Failed to upload image.");
      } finally {
          setUploading(false);
      }
  };

  const handleImageUpload = (e) => {
      processFile(e.target.files[0]);
  };

  const handleCameraCapture = (file) => {
      setIsCameraOpen(false);
      processFile(file);
  };

  const handleSubmit = async (e) => {
      e.preventDefault();
      if (!logisticsData.proof_url) return alert("Please upload a photo of the Waybill Receipt.");

      setSubmitting(true);
      try {
          await ordersApi.updateLogistics(selectedOrder.id, logisticsData);
          alert("Order marked as Shipped!");
          setIsModalOpen(false);
          fetchOrders();
      } catch (err) {
          alert("Failed to update logistics.");
      } finally {
          setSubmitting(false);
      }
  };

  if (loading) return <div className="p-8 text-center"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading Orders...</div>;

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <FaBox className="text-primary" /> Store Orders & Fulfillment
      </h2>

      {orders.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded border border-dashed">
              <p className="text-gray-500">No orders found for your store.</p>
          </div>
      ) : (
          <div className="space-y-4">
              {orders.map(order => (
                  <div key={order.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                      <div className="flex justify-between items-start mb-3">
                          <div>
                              <div className="flex items-center gap-2">
                                  <h3 className="font-bold text-gray-800 text-lg">{order.order_number}</h3>
                                  <span className={`text-xs px-2 py-0.5 rounded font-bold uppercase ${order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                                      {order.status}
                                  </span>
                              </div>
                              <p className="text-xs text-gray-500">{new Date(order.created_at).toLocaleString()}</p>
                          </div>
                          
                          {/* SLA TIMER */}
                          {order.shipping_deadline && (
                              <div className="text-right">
                                  <span className="text-[10px] text-gray-400 uppercase font-bold block mb-1">Shipping Deadline</span>
                                  <SlaTimer deadline={order.shipping_deadline} status={order.status} />
                              </div>
                          )}
                      </div>

                      {/* Items */}
                      <div className="bg-gray-50 p-3 rounded mb-3">
                          {order.items.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm mb-1 last:mb-0">
                                  <span>{item.quantity}x {item.name}</span>
                                  <span className="font-bold">{helpers.formatCurrency(item.price * item.quantity, 'NGN')}</span>
                              </div>
                          ))}
                      </div>

                      {/* Customer Location */}
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                          <FaMapMarkerAlt className="text-red-500" />
                          {order.shipping_address?.city}, {order.shipping_address?.state}
                      </div>

                      {/* Action Button */}
                      {order.status === 'pending' || order.status === 'processing' || order.status === 'paid' ? (
                          <button 
                              onClick={() => handleOpenModal(order)}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 rounded flex items-center justify-center gap-2"
                          >
                              <FaTruck /> Ship Order (Add Waybill)
                          </button>
                      ) : order.status === 'shipped' ? (
                          <div className="bg-green-50 text-green-700 p-2 rounded text-center text-sm font-bold border border-green-200">
                              <FaCheckCircle className="inline mr-2" /> Shipped via {order.carrier_name}
                          </div>
                      ) : null}
                  </div>
              ))}
          </div>
      )}

      {/* --- WAYBILL MODAL (FIXED LAYOUT) --- */}
      {isModalOpen && (
          <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black bg-opacity-50">
              <div className="flex min-h-full items-center justify-center p-4">
                  <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative">
                      
                      <div className="flex justify-between items-center mb-4 border-b pb-2">
                          <h3 className="text-lg font-bold">Add Shipping Details</h3>
                          <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                              <FaTimes size={20} />
                          </button>
                      </div>
                      
                      <form onSubmit={handleSubmit} className="space-y-4">
                          <div className="bg-blue-50 p-3 rounded text-sm text-blue-800 flex items-start gap-2">
                              <FaInfoCircle className="mt-1 flex-shrink-0" />
                              <p>Please drop off the item at the transport park and enter the details below. <strong>Upload the receipt</strong> to verify shipping.</p>
                          </div>

                          <div>
                              <label className="block text-xs font-bold text-gray-600 mb-1">Transport Company</label>
                              <input 
                                  required 
                                  value={logisticsData.carrier_name}
                                  onChange={e => setLogisticsData({...logisticsData, carrier_name: e.target.value})}
                                  className="w-full p-2 border rounded"
                                  placeholder="e.g. GIG Logistics, Peace Mass"
                              />
                          </div>

                          <div>
                              <label className="block text-xs font-bold text-gray-600 mb-1">Waybill / Tracking Number</label>
                              <input 
                                  required 
                                  value={logisticsData.tracking_number}
                                  onChange={e => setLogisticsData({...logisticsData, tracking_number: e.target.value})}
                                  className="w-full p-2 border rounded font-mono"
                                  placeholder="e.g. 123456789"
                              />
                          </div>

                          <div>
                              <label className="block text-xs font-bold text-gray-600 mb-1">Pickup Terminal / Location</label>
                              <input 
                                  required 
                                  value={logisticsData.pickup_terminal}
                                  onChange={e => setLogisticsData({...logisticsData, pickup_terminal: e.target.value})}
                                  className="w-full p-2 border rounded"
                                  placeholder="e.g. Utako Branch, Abuja"
                              />
                              <p className="text-[10px] text-gray-500 mt-1">Where should the customer go to pick this up?</p>
                          </div>

                          {/* Receipt Upload */}
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                              {uploading ? (
                                  <div className="text-primary flex flex-col items-center">
                                      <FaSpinner className="animate-spin text-xl mb-1" />
                                      <span className="text-xs">Uploading Receipt...</span>
                                  </div>
                              ) : logisticsData.proof_url ? (
                                  <div className="relative">
                                      <img src={logisticsData.proof_url} alt="Receipt" className="h-32 mx-auto rounded object-contain" />
                                      <button 
                                          type="button" 
                                          onClick={() => setLogisticsData(prev => ({ ...prev, proof_url: '' }))}
                                          className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-full text-xs"
                                      >
                                          <FaTimes />
                                      </button>
                                  </div>
                              ) : (
                                  <div className="flex flex-col items-center gap-3">
                                      {/* SNAP BUTTON */}
                                      <button 
                                          type="button"
                                          onClick={() => setIsCameraOpen(true)}
                                          className="bg-blue-600 text-white px-4 py-2 rounded-full font-bold flex items-center gap-2 shadow-md hover:bg-blue-700 transition-transform hover:scale-105"
                                      >
                                          <FaCamera /> Snap Receipt
                                      </button>
                                      
                                      <span className="text-xs text-gray-400">- OR -</span>

                                      {/* UPLOAD BUTTON */}
                                      <label className="cursor-pointer text-gray-500 hover:text-primary flex flex-col items-center">
                                          <FaFolderOpen className="text-2xl mb-1" />
                                          <span className="text-xs font-bold">Browse Files</span>
                                          <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                                      </label>
                                  </div>
                              )}
                          </div>

                          <button 
                              type="submit" 
                              disabled={submitting || !logisticsData.proof_url}
                              className={`w-full py-2 rounded font-bold text-white flex items-center justify-center gap-2 ${submitting || !logisticsData.proof_url ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                          >
                              {submitting ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />} Confirm Shipment
                          </button>
                  </form>
              </div>
          </div>
      </div>
      )}

      {/* --- CAMERA MODAL --- */}
      <CameraModal 
          isOpen={isCameraOpen} 
          onClose={() => setIsCameraOpen(false)} 
          onCapture={handleCameraCapture}
          viewType="waybill_receipt"
      />

    </div>
  );
}

export default StoreOrders;